import { useTranslation } from 'react-i18next';
import { Link } from 'react-router-dom';
import { useIsHongKongIp } from '../../hooks/useIsHongKongIp';
import { normalizeSiteLanguage } from '../../utils/currentSiteLanguage';

export default function Footer() {
  const { t, i18n } = useTranslation();
  const language = normalizeSiteLanguage(i18n.resolvedLanguage || i18n.language);
  const isChinese = language === 'hk' || language === 'cn';
  const { canShowRestrictedContent } = useIsHongKongIp();

  return (
    <footer className="bg-gray-900 text-white">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-16">
        <div
          className={`grid grid-cols-1 gap-8 ${
            canShowRestrictedContent ? 'md:grid-cols-4' : 'md:grid-cols-3'
          }`}
        >
          <div>
            <div className="flex items-center space-x-3 mb-6">
              <div className="w-10 h-10 flex items-center justify-center shrink-0">
                <img
                  src="/img/0c35285da7db83dd4744dcd7f9c41cc3.png"
                  alt={isChinese ? '金利寶環球 Gold Label Global Limited logo' : 'Gold Label Global logo'}
                  className="w-full h-full object-contain filter drop-shadow-lg"
                />
              </div>
              <div>
                <span
                  dir={isChinese ? undefined : 'ltr'}
                  className="text-xl font-semibold text-white block"
                  style={{ fontFamily: '"Times New Roman", serif' }}
                >
                  {isChinese ? 'GOLD LABEL GLOBAL LIMITED' : 'Gold Label Global'}
                </span>
                {isChinese && (
                  <span className="text-sm text-orange-300 font-semibold block">
                    金利寶環球
                  </span>
                )}
                <span
                  className="text-sm text-gray-400"
                  style={{ fontFamily: '"Times New Roman", serif' }}
                >
                  {t('footer.regNo')}： 68781521
                </span>
              </div>
            </div>
            <p className="text-gray-400 mb-6 leading-relaxed">{t('footer.license')}</p>
            <div className="flex space-x-4">
              <a href="#" className="w-10 h-10 bg-gray-800 rounded-lg flex items-center justify-center hover:bg-orange-600 transition-colors cursor-pointer">
                <i className="ri-twitter-line" />
              </a>
              <a href="#" className="w-10 h-10 bg-gray-800 rounded-lg flex items-center justify-center hover:bg-orange-600 transition-colors cursor-pointer">
                <i className="ri-linkedin-line" />
              </a>
              <a href="#" className="w-10 h-10 bg-gray-800 rounded-lg flex items-center justify-center hover:bg-orange-600 transition-colors cursor-pointer">
                <i className="ri-facebook-line" />
              </a>
            </div>
          </div>

          <div>
            <h3 className="text-lg font-semibold mb-6">{t('footer.company')}</h3>
            <ul className="space-y-3">
              <li>
                <Link to="/about" className="text-gray-400 hover:text-orange-400 transition-colors cursor-pointer">
                  {t('footer.about')}
                </Link>
              </li>
              {canShowRestrictedContent && (
                <li>
                  <Link to="/market" className="text-gray-400 hover:text-orange-400 transition-colors cursor-pointer">
                    {t('nav.markets')}
                  </Link>
                </li>
              )}
              <li>
                <Link to="/grand-opening" className="text-gray-400 hover:text-orange-400 transition-colors cursor-pointer">
                  {t('nav.news')}
                </Link>
              </li>
            </ul>
          </div>

          {canShowRestrictedContent && (
            <div>
              <h3 className="text-lg font-semibold mb-6">{t('footer.trading')}</h3>
              <ul className="space-y-3">
                <li>
                  <Link to="/platform" className="text-gray-400 hover:text-orange-400 transition-colors cursor-pointer">
                    {t('footer.platforms')}
                  </Link>
                </li>
                <li>
                  <a href="#" className="text-gray-400 hover:text-orange-400 transition-colors cursor-pointer">
                    {t('footer.education')}
                  </a>
                </li>
                <li>
                  <a href="#" className="text-gray-400 hover:text-orange-400 transition-colors cursor-pointer">
                    {t('footer.analysis')}
                  </a>
                </li>
              </ul>
            </div>
          )}

          <div>
            <h3 className="text-lg font-semibold mb-6">{t('footer.support')}</h3>
            <ul className="space-y-3">
              <li>
                <Link to="/contact" className="text-gray-400 hover:text-orange-400 transition-colors cursor-pointer">
                  {t('footer.contact')}
                </Link>
              </li>
              <li>
                <Link to="/faq" className="text-gray-400 hover:text-orange-400 transition-colors cursor-pointer">
                  {t('footer.faq')}
                </Link>
              </li>
            </ul>
          </div>
        </div>

        <div className="border-t border-gray-800 mt-12 pt-8">
          <div className="bg-gray-800 rounded-lg p-6 mb-8">
            <h4 className="text-orange-400 font-semibold mb-3">{t('footer.riskwarning')}</h4>
            <p className="text-gray-300 text-sm leading-relaxed">{t('footer.disclaimer')}</p>
          </div>

          <div className="bg-gray-800 rounded-lg p-6 mb-8">
            <h4 className="text-orange-400 font-semibold mb-3">{t('footer.jurisdiction')}</h4>
            <p className="text-gray-300 text-sm leading-relaxed">{t('footer.jurisdictionDisclaimer')}</p>
          </div>

          <div className="bg-gray-800 rounded-lg p-6 mb-8">
            <h4 className="text-orange-400 font-semibold mb-3">{t('footer.relatedEntity')}</h4>
            <p className="text-gray-300 text-sm leading-relaxed">{t('footer.saintLuciaEntity')}</p>
          </div>
        </div>

        <div className="border-t border-gray-800 pt-8">
          <div className="flex flex-col md:flex-row justify-between items-center">
            <div className="flex flex-wrap gap-6 mb-4 md:mb-0">
              <Link to="/terms" className="text-gray-400 hover:text-orange-400 transition-colors text-sm cursor-pointer">
                {t('footer.terms')}
              </Link>
              <Link to="/privacy" className="text-gray-400 hover:text-orange-400 transition-colors text-sm cursor-pointer">
                {t('footer.privacy')}
              </Link>
              <Link to="/risk-disclosure" className="text-gray-400 hover:text-orange-400 transition-colors text-sm cursor-pointer">
                {t('footer.risk')}
              </Link>
            </div>
            <p className="text-gray-400 text-sm">{t('footer.copyright')}</p>
          </div>
        </div>
      </div>
    </footer>
  );
}
